import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { IFlowLockup } from '@/shared/ui/brand';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import { apiClient } from '@/shared/api/client';
import { queryKeys } from '@/shared/api/query-keys';
import { useAppStore } from '@/shared/store/app-store';
import { useAuthStore } from '@/features/auth/auth-store';
import { NAV_ITEMS } from './nav';

export function Sidebar() {
  const collapsed = useAppStore((s) => s.sidebarCollapsed);
  const toggle = useAppStore((s) => s.toggleSidebar);
  const user = useAuthStore((s) => s.user);

  const role = user?.role === 'client' ? 'client' : 'superadmin';
  const visibleItems = NAV_ITEMS.filter((item) => !item.role || item.role === role);

  // Live pending-requests count for the super-admin "Requests" nav badge.
  const requestsQuery = useQuery({
    queryKey: queryKeys.modelRequests('pending'),
    queryFn: ({ signal }) => apiClient.getModelRequests('pending', signal),
    enabled: role === 'superadmin',
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const pendingCount = requestsQuery.data?.pendingCount ?? 0;

  return (
    <motion.aside
      animate={{ width: collapsed ? 68 : 240 }}
      transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
      className="z-sticky flex shrink-0 flex-col border-r border-border bg-surface"
    >
      <div
        className={cn(
          'flex h-16 items-center border-b border-border px-4',
          collapsed && 'justify-center px-0',
        )}
      >
        <IFlowLockup collapsed={collapsed} />
      </div>

      <nav className="flex-1 space-y-1 p-3" aria-label="Primary">
        {visibleItems.map((item) => {
          const showBadge = item.badge === 'modelRequests' && pendingCount > 0;
          const link = (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  collapsed && 'justify-center px-0',
                  isActive
                    ? 'bg-gradient-to-r from-brand-500/15 to-brand-500/5 font-semibold text-brand-accent shadow-sm ring-1 ring-brand-500/15'
                    : 'text-muted-foreground hover:bg-surface-2 hover:text-foreground',
                )
              }
            >
              <item.icon className="size-[18px] shrink-0" aria-hidden />
              {!collapsed && <span className="truncate">{item.label}</span>}
              {showBadge && !collapsed && (
                <span className="ml-auto rounded-full bg-brand-500/20 px-1.5 py-0.5 text-xs font-semibold text-brand-accent">
                  {pendingCount}
                </span>
              )}
              {showBadge && collapsed && (
                <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-brand-400" aria-hidden />
              )}
            </NavLink>
          );
          return collapsed ? (
            <Tooltip key={item.to} delayDuration={0}>
              <TooltipTrigger asChild>{link}</TooltipTrigger>
              <TooltipContent side="right">{item.label}</TooltipContent>
            </Tooltip>
          ) : (
            link
          );
        })}
      </nav>

      <div className="space-y-3 border-t border-border p-3">
        <button
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={cn(
            'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground',
            collapsed && 'justify-center px-0',
          )}
        >
          {collapsed ? (
            <PanelLeftOpen className="size-[18px]" />
          ) : (
            <PanelLeftClose className="size-[18px]" />
          )}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </motion.aside>
  );
}
